import React, { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, Users, DollarSign, Clock, AlertTriangle, Shield,
  Eye, Pause, Play, Loader2, Mail, X, LogIn, RefreshCw, Crown,
  Lock, TrendingUp, UserCheck, Sparkles, Zap, Bell, Sun, Moon,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";

const VAULT_FLAG = "wc_platform_vault_token"; // sessionStorage — clears on tab close

/**
 * Platform Admin Console — only reachable by users flagged `is_platform_admin`.
 * Lists every tenant with its billing status + exposes impersonate /
 * suspend / unsuspend controls. The route gate is enforced client-side
 * (nav link only shows for platform admins) AND server-side (403 from the
 * /api/platform/* endpoints if the flag's missing).
 *
 * Secondary password gate (iter-83): even an authenticated platform
 * admin has to pass a second password check before any Vault data
 * surfaces. The unlock state is held in sessionStorage so the gate
 * reappears on every new tab / fresh browser session.
 */
export default function PlatformAdminPage() {
  const { user } = useAuth();
  const [unlocked, setUnlocked] = useState(() => {
    try { return !!sessionStorage.getItem(VAULT_FLAG); } catch { return false; }
  });
  useEffect(() => {
    const relock = () => setUnlocked(false);
    window.addEventListener("wc:vault-locked", relock);
    return () => window.removeEventListener("wc:vault-locked", relock);
  }, []);

  if (!user?.is_platform_admin) return <Navigate to="/" replace />;
  if (!unlocked) return <VaultLockScreen onUnlock={() => setUnlocked(true)} />;
  return <PlatformVault onLock={() => {
    try { sessionStorage.removeItem(VAULT_FLAG); } catch { /* ignore */ }
    setUnlocked(false);
  }} />;
}


/* -------------------------------------------------------------------------- */
/* VaultLockScreen — secondary password gate                                  */
/* -------------------------------------------------------------------------- */
function VaultLockScreen({ onUnlock }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!pw) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/platform/vault/unlock", { password: pw });
      try { sessionStorage.setItem(VAULT_FLAG, data.vault_token); } catch { /* ignore */ }
      onUnlock();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't unlock the vault.");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center justify-center py-20" data-testid="vault-lock">
      <div className="w-full max-w-md glass-strong rounded-3xl border border-sky-400/20 shadow-[0_0_40px_-20px_rgba(56,189,248,0.4)] overflow-hidden">
        <div className="px-8 py-7 text-center border-b border-white/5 bg-sky-500/[0.05]">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-sky-100 border border-sky-300 dark:bg-sky-500/15 dark:border-sky-400/30 flex items-center justify-center">
            <Lock className="w-6 h-6 text-sky-700 dark:text-sky-300" />
          </div>
          <h1 className="font-heading text-2xl text-white mt-4">Platform Vault</h1>
          <p className="text-sm text-slate-400 mt-1.5">
            One more password to unlock live revenue, every shop, every user. Session-only — the gate reappears on new tabs.
          </p>
        </div>
        <form onSubmit={submit} className="px-8 py-6 space-y-4">
          <label className="block">
            <div className="text-label mb-2">Vault password</div>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="password"
                autoFocus
                data-testid="vault-password-input"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="••••••••••"
                className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-sky-400/60"
              />
            </div>
          </label>
          {err && (
            <div data-testid="vault-error" className="flex items-start gap-2 rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-rose-200 text-xs">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>{err}</span>
            </div>
          )}
          <button
            type="submit"
            disabled={busy || !pw}
            data-testid="vault-unlock-btn"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-sky-600 hover:bg-sky-500 text-white font-semibold text-sm px-4 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-sky-500/20"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            {busy ? "Unlocking…" : "Unlock Vault"}
          </button>
          <p className="text-[11px] text-slate-500 text-center">
            Failed attempts are written to the <code>platform_audit</code> log.
          </p>
        </form>
      </div>
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* PlatformVault — 4-section console once unlocked                            */
/* -------------------------------------------------------------------------- */
const SECTION_META = {
  overview:  { kicker: "Overview",  title: "Every shop. Every user. Every dollar.", sub: "The live pulse of WrapConsole — subscriptions, signups and trials at a glance." },
  analytics: { kicker: "Analytics", title: "Revenue & growth.",                     sub: "Net cash from Stripe, top-grossing shops and the full user directory." },
  commands:  { kicker: "Commands",  title: "Run the fleet.",                        sub: "View any shop read-only, suspend or reinstate access. Every action is audited." },
  settings:  { kicker: "Settings",  title: "Platform settings.",                    sub: "Vault security, platform-wide email delivery and your master account." },
};

function PlatformVault({ onLock }) {
  const { section = "overview" } = useParams();
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [query, setQuery] = useState("");
  const [detailTenant, setDetailTenant] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, t, u, r, a] = await Promise.all([
        api.get("/platform/stats"),
        api.get("/platform/tenants"),
        api.get("/platform/vault/users"),
        api.get("/platform/vault/revenue"),
        api.get("/platform/audit").catch(() => ({ data: [] })),
      ]);
      setStats(s.data);
      setTenants(t.data || []);
      setUsers(u.data?.users || []);
      setRevenue(r.data);
      setAudit(a.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setQuery(""); }, [section]);

  if (!SECTION_META[section]) return <Navigate to="/platform/overview" replace />;
  const meta = SECTION_META[section];

  const suspend = async (t) => {
    const reason = window.prompt(`Reason for suspending ${t.name}?`, "");
    if (reason === null) return;
    setBusy(t.id);
    try {
      await api.post(`/platform/tenants/${t.id}/suspend`, { reason });
      toast.success(`${t.name} suspended`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(""); }
  };

  const unsuspend = async (t) => {
    setBusy(t.id);
    try {
      await api.post(`/platform/tenants/${t.id}/unsuspend`);
      toast.success(`${t.name} unsuspended`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(""); }
  };

  const impersonate = async (t) => {
    const reason = window.prompt(`View ${t.name} read-only? (optional reason for audit log)`, "");
    if (reason === null) return;
    setBusy(t.id);
    try {
      const { data } = await api.post(`/platform/impersonate/${t.id}`, { reason: reason || "" });
      const original = localStorage.getItem("wc_token");
      if (original && !localStorage.getItem("wc_token_platform")) {
        localStorage.setItem("wc_token_platform", original);
      }
      localStorage.setItem("wc_token", data.token);
      toast.success(`Viewing ${data.tenant_name} (read-only)`);
      window.location.href = "/";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
      setBusy("");
    }
  };

  const q = query.trim().toLowerCase();
  const filteredTenants = tenants.filter((t) => !q
    || (t.name || "").toLowerCase().includes(q) || (t.slug || "").toLowerCase().includes(q));
  const filteredUsers = users.filter((u) => !q
    || (u.email || "").toLowerCase().includes(q)
    || (u.name || "").toLowerCase().includes(q)
    || (u.memberships || []).some((m) => (m.tenant_name || "").toLowerCase().includes(q)));

  return (
    <div className="space-y-6" data-testid={`platform-section-${section}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap" data-testid="platform-admin-page">
        <div>
          <div className="text-label flex items-center gap-2 text-sky-700 dark:text-sky-300">
            <Crown className="w-3 h-3" /> Platform · {meta.kicker}
          </div>
          <h1 data-testid="platform-section-title" className="font-heading text-4xl md:text-5xl font-light tracking-tighter mt-2">{meta.title}</h1>
          <p className="text-slate-400 mt-2 max-w-xl">{meta.sub}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={load}
            disabled={loading}
            data-testid="platform-reload-btn"
            className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white rounded-full px-4 py-2 text-xs font-semibold transition disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Reload
          </button>
          <button
            onClick={onLock}
            data-testid="platform-lock-btn"
            title="Re-lock the vault for this tab"
            className="flex items-center gap-1.5 bg-sky-50 hover:bg-sky-100 border border-sky-300 text-sky-800 dark:bg-sky-500/10 dark:hover:bg-sky-500/20 dark:border-sky-400/30 dark:text-sky-200 rounded-full px-4 py-2 text-xs font-semibold transition"
          >
            <Lock className="w-3.5 h-3.5" /> Lock vault
          </button>
        </div>
      </div>

      {section === "overview" && <OverviewSection stats={stats} tenants={tenants} audit={audit} />}

      {section === "analytics" && (
        <>
          {revenue && <RevenueSection revenue={revenue} />}
          <SearchBox value={query} onChange={setQuery} placeholder="Search users by name, email, or shop…" />
          <UsersTable users={filteredUsers} loading={loading} query={query} />
        </>
      )}

      {section === "commands" && (
        <>
          <SearchBox value={query} onChange={setQuery} placeholder="Search by shop name…" />
          <ShopsTable
            tenants={filteredTenants} loading={loading} query={query} busy={busy}
            onImpersonate={impersonate} onSuspend={suspend} onUnsuspend={unsuspend} onDetail={setDetailTenant}
          />
          <AuditTable audit={audit} />
        </>
      )}

      {section === "settings" && <PlatformSettings onLock={onLock} />}

      {detailTenant && <TenantDetailModal tenant={detailTenant} onClose={() => setDetailTenant(null)} />}
    </div>
  );
}


function SearchBox({ value, onChange, placeholder }) {
  return (
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid="platform-search"
      className="bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30 w-full md:max-w-md"
    />
  );
}


function OverviewSection({ stats, tenants, audit }) {
  if (!stats) return null;
  const recent = [...tenants].sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""))).slice(0, 5);
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="platform-hero-stats">
        <HeroStat label="MRR" value={`$${Number(stats.mrr_aud).toLocaleString()}`} sub={`$${Number(stats.arr_aud).toLocaleString()} ARR`} icon={DollarSign} tone="emerald" />
        <HeroStat label="Paying shops" value={stats.active} sub={`${stats.trialing} on trial`} icon={Building2} tone="blue" />
        <HeroStat label="Total users" value={stats.total_users} sub={`across ${stats.total} shops`} icon={Users} tone="violet" />
        <HeroStat
          label="New (30d)"
          value={stats.recent_signups_30d}
          sub={stats.trials_ending_soon > 0 ? `${stats.trials_ending_soon} trial${stats.trials_ending_soon === 1 ? "" : "s"} ending ≤3d` : "No trials ending soon"}
          icon={TrendingUp}
          tone={stats.trials_ending_soon > 0 ? "rose" : "slate"}
        />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="platform-sub-stats">
        <MiniStat label="Starter"   value={stats.plan_split?.starter ?? 0} />
        <MiniStat label="Pro"       value={stats.plan_split?.pro ?? 0} tone="info" />
        <MiniStat label="Trialing"  value={stats.trialing} tone="info" />
        <MiniStat label="Past due"  value={stats.past_due} tone="danger" />
        <MiniStat label="Cancelled" value={stats.canceled + stats.suspended} tone="danger" />
        <MiniStat label="Pipeline MRR" value={`$${Number(stats.pipeline_mrr_aud).toLocaleString()}`} tone="muted" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-white/5" data-testid="platform-recent-shops">
          <div className="px-5 py-3 border-b border-white/5 text-label flex items-center gap-2"><Building2 className="w-3 h-3" /> Newest shops</div>
          {recent.length === 0 && <div className="px-5 py-8 text-sm text-slate-500 italic">No shops yet.</div>}
          {recent.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-3 px-5 py-3 border-t border-white/[0.04] first:border-0 text-sm">
              <div className="min-w-0">
                <div className="text-white truncate">{t.name}</div>
                <div className="text-[11px] text-slate-500">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}</div>
              </div>
              <StatusPill status={t.status} />
            </div>
          ))}
        </div>
        <div className="rounded-2xl border border-white/5" data-testid="platform-recent-activity">
          <div className="px-5 py-3 border-b border-white/5 text-label flex items-center gap-2"><Bell className="w-3 h-3" /> Recent platform activity</div>
          {audit.length === 0 && <div className="px-5 py-8 text-sm text-slate-500 italic">No activity yet.</div>}
          {audit.slice(0, 5).map((a) => (
            <div key={a.id} className="px-5 py-3 border-t border-white/[0.04] first:border-0 text-xs text-slate-400">
              <span className="text-white capitalize">{(a.action || "").replace(/_/g, " ")}</span>
              {a.tenant_name && <span> · {a.tenant_name}</span>}
              <span className="text-slate-500 float-right">{a.created_at ? new Date(a.created_at).toLocaleString() : ""}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}


function ShopsTable({ tenants, loading, query, busy, onImpersonate, onSuspend, onUnsuspend, onDetail }) {
  return (
    <div className="rounded-2xl border border-white/5 overflow-x-auto" data-testid="platform-shops-table">
      <table className="w-full min-w-[900px]">
        <thead className="bg-white/[0.02] text-[10px] uppercase tracking-widest text-slate-500">
          <tr>
            <th className="text-left px-5 py-3">Shop</th>
            <th className="text-left px-5 py-3">Plan</th>
            <th className="text-left px-5 py-3">Status</th>
            <th className="text-left px-5 py-3">Trial / Period</th>
            <th className="text-left px-5 py-3">Created</th>
            <th className="text-right px-5 py-3">Commands</th>
          </tr>
        </thead>
        <tbody>
          {!loading && tenants.length === 0 && (
            <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500 italic text-sm">
              {query ? "No shops match that search." : "No tenants yet — invite the first shop."}
            </td></tr>
          )}
          {tenants.map((t) => {
            const suspended = t.status === "suspended";
            return (
              <tr key={t.id} data-testid={`platform-tenant-${t.id}`} className="border-t border-white/[0.04] hover:bg-white/[0.03]">
                <td className="px-5 py-3 text-white">
                  <div className="font-semibold">{t.name}</div>
                  <div className="text-[11px] text-slate-500">{t.slug}</div>
                </td>
                <td className="px-5 py-3"><span className={`pill ${t.plan === "pro" ? "pill-info" : "pill-muted"} capitalize`}>{t.plan}</span></td>
                <td className="px-5 py-3"><StatusPill status={t.status} /></td>
                <td className="px-5 py-3 text-xs text-slate-300">{t.trial_ends_at ? `trial ends ${new Date(t.trial_ends_at).toLocaleDateString()}` : "—"}</td>
                <td className="px-5 py-3 text-xs text-slate-400">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-1.5 justify-end flex-wrap">
                    <button onClick={() => onDetail(t)} data-testid={`platform-detail-${t.id}`}
                      className="inline-flex items-center gap-1 bg-white/5 hover:bg-white/10 text-slate-200 border border-white/10 rounded-full px-3 py-1 text-[11px] font-medium">
                      <Building2 className="w-3 h-3" /> Details
                    </button>
                    <button onClick={() => onImpersonate(t)} disabled={busy === t.id} data-testid={`platform-impersonate-${t.id}`}
                      className="inline-flex items-center gap-1 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-700 dark:text-emerald-200 border border-emerald-400/30 rounded-full px-3 py-1 text-[11px] font-medium disabled:opacity-50">
                      <Eye className="w-3 h-3" /> View read-only
                    </button>
                    {suspended ? (
                      <button onClick={() => onUnsuspend(t)} disabled={busy === t.id} data-testid={`platform-unsuspend-${t.id}`}
                        className="inline-flex items-center gap-1 bg-sky-500/15 hover:bg-sky-500/25 text-sky-700 dark:text-sky-200 border border-sky-400/30 rounded-full px-3 py-1 text-[11px] font-medium disabled:opacity-50">
                        <Play className="w-3 h-3" /> Unsuspend
                      </button>
                    ) : (
                      <button onClick={() => onSuspend(t)} disabled={busy === t.id} data-testid={`platform-suspend-${t.id}`}
                        className="inline-flex items-center gap-1 bg-rose-500/15 hover:bg-rose-500/25 text-rose-700 dark:text-rose-200 border border-rose-400/30 rounded-full px-3 py-1 text-[11px] font-medium disabled:opacity-50">
                        <Pause className="w-3 h-3" /> Suspend
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


function AuditTable({ audit }) {
  return (
    <div className="rounded-2xl border border-white/5" data-testid="platform-audit-log">
      <div className="px-5 py-3 border-b border-white/5 text-label flex items-center gap-2"><Shield className="w-3 h-3" /> Command audit log</div>
      {audit.length === 0 && <div className="px-5 py-8 text-sm text-slate-500 italic">No commands run yet.</div>}
      {audit.slice(0, 25).map((a) => (
        <div key={a.id} className="px-5 py-2.5 border-t border-white/[0.04] first:border-0 text-[11px] text-slate-400">
          <span className="text-white capitalize">{(a.action || "").replace(/_/g, " ")}</span>
          {a.tenant_name && <span> · {a.tenant_name}</span>} by {a.actor_email}
          {a.reason && <span className="text-slate-500"> — "{a.reason}"</span>}
          <span className="text-slate-500 float-right">{a.created_at ? new Date(a.created_at).toLocaleString() : ""}</span>
        </div>
      ))}
    </div>
  );
}


function UsersTable({ users, loading, query }) {
  return (
    <div className="rounded-2xl border border-white/5 overflow-x-auto" data-testid="platform-users-table">
      <table className="w-full min-w-[900px]">
        <thead className="bg-white/[0.02] text-[10px] uppercase tracking-widest text-slate-500">
          <tr>
            <th className="text-left px-5 py-3">User</th>
            <th className="text-left px-5 py-3">Shop</th>
            <th className="text-left px-5 py-3">Role</th>
            <th className="text-left px-5 py-3">Joined</th>
            <th className="text-left px-5 py-3">Last login</th>
          </tr>
        </thead>
        <tbody>
          {!loading && users.length === 0 && (
            <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500 italic text-sm">
              {query ? "No users match that search." : "No users yet."}
            </td></tr>
          )}
          {users.map((u) => {
            const rows = u.memberships?.length ? u.memberships : [{
              tenant_name: u.is_platform_admin ? "— platform —" : "—", tenant_plan: "", role: u.role, joined_at: u.onboarded_at,
            }];
            return rows.map((m, idx) => (
              <tr key={`${u.id}-${idx}`} data-testid={`platform-user-${u.id}-${idx}`} className="border-t border-white/[0.04] hover:bg-white/[0.03]">
                <td className="px-5 py-3 text-white">
                  <div className="flex items-center gap-2">
                    <div className="font-semibold">{u.name || "—"}</div>
                    {u.is_platform_admin && <span className="pill pill-info text-[9px]"><Crown className="w-2 h-2 inline mr-0.5" />Platform</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-1"><Mail className="w-3 h-3" />{u.email}</div>
                </td>
                <td className="px-5 py-3 text-xs text-slate-200">
                  <div>{m.tenant_name}</div>
                  {m.tenant_plan && <span className={`pill ${m.tenant_plan === "pro" ? "pill-info" : "pill-muted"} text-[9px] mt-0.5 capitalize`}>{m.tenant_plan}</span>}
                </td>
                <td className="px-5 py-3"><span className="pill pill-muted text-[10px] capitalize">{m.role || "—"}</span></td>
                <td className="px-5 py-3 text-xs text-slate-400">{m.joined_at ? new Date(m.joined_at).toLocaleDateString() : "—"}</td>
                <td className="px-5 py-3 text-xs text-slate-400">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "never"}</td>
              </tr>
            ));
          })}
        </tbody>
      </table>
    </div>
  );
}


function RevenueSection({ revenue }) {
  if (revenue.configured === false) {
    return (
      <div className="rounded-2xl border border-sky-300 bg-sky-50 dark:border-sky-400/30 dark:bg-sky-500/[0.05] p-6 text-center" data-testid="platform-revenue">
        <AlertTriangle className="w-6 h-6 text-sky-700 dark:text-sky-300 mx-auto mb-2" />
        <h3 className="text-white font-medium text-lg">Stripe is not configured</h3>
        <p className="text-sm text-slate-400 mt-1 max-w-md mx-auto">{revenue.note}</p>
      </div>
    );
  }
  if (revenue.stripe_error) {
    return (
      <div className="rounded-2xl border border-rose-400/30 bg-rose-500/[0.05] p-6" data-testid="platform-revenue">
        <h3 className="text-white font-medium">Stripe reachability error</h3>
        <p className="text-rose-200 mt-1 font-mono text-xs">{revenue.stripe_error}</p>
        <p className="text-xs text-slate-400 mt-2">{revenue.note}</p>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="platform-revenue">
      <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/[0.04] p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-label text-emerald-700 dark:text-emerald-300 flex items-center gap-2"><DollarSign className="w-3 h-3" /> Net cash collected (lifetime)</div>
            <div data-testid="platform-net-revenue" className="font-heading text-5xl text-white font-light tracking-tighter mt-2">
              ${Number(revenue.net_aud).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              <span className="text-sm text-slate-400 ml-2">AUD</span>
            </div>
            <p className="text-xs text-emerald-700/80 dark:text-emerald-300/70 mt-2 flex items-center gap-1.5"><Zap className="w-3 h-3" /> {revenue.note}</p>
          </div>
          {revenue.by_month?.length > 0 && <RevenueSparkline data={revenue.by_month} />}
        </div>
        <div className="grid grid-cols-3 gap-3 mt-5 pt-5 border-t border-emerald-400/10">
          <BreakdownStat label="Gross" value={revenue.gross_aud} tone="slate" />
          <BreakdownStat label="Stripe fees" value={revenue.fees_aud} tone="muted" negative />
          <BreakdownStat label="Refunds" value={revenue.refunds_aud} tone="muted" negative />
        </div>
        <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-500">{revenue.transaction_count || 0} transactions scanned</div>
      </div>
      <div className="rounded-2xl border border-white/5">
        <div className="px-5 py-3 border-b border-white/5 text-label flex items-center gap-2"><Zap className="w-3 h-3" /> Top-grossing shops (lifetime net)</div>
        <table className="w-full">
          <thead className="bg-white/[0.02] text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-5 py-3">Shop</th>
              <th className="text-left px-5 py-3">Plan</th>
              <th className="text-left px-5 py-3">Stripe customer</th>
              <th className="text-right px-5 py-3">Net collected</th>
            </tr>
          </thead>
          <tbody>
            {(revenue.top_tenants || []).length === 0 && (
              <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-500 italic text-sm">No matched Stripe customers yet. Once a shop pays an invoice, they'll appear here.</td></tr>
            )}
            {(revenue.top_tenants || []).map((t) => (
              <tr key={t.stripe_customer_id} className="border-t border-white/[0.04]">
                <td className="px-5 py-3 text-white font-medium">{t.tenant_name}</td>
                <td className="px-5 py-3"><span className={`pill ${t.plan === "pro" ? "pill-info" : "pill-muted"} text-[10px] capitalize`}>{t.plan}</span></td>
                <td className="px-5 py-3 text-[11px] text-slate-500 font-mono">{t.stripe_customer_id}</td>
                <td className="px-5 py-3 text-right text-white font-semibold">${Number(t.net_aud).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function PlatformSettings({ onLock }) {
  const { theme, toggle } = useTheme();
  const [cfg, setCfg] = useState(null);
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => {
    api.get("/platform/settings").then(({ data }) => { setCfg(data); setTo(data.account_email || ""); })
      .catch(() => toast.error("Couldn't load platform settings"));
  }, []);
  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/platform/settings/test-email", { recipient: to });
      if (data.result?.sent) toast.success(`Test email sent to ${data.recipient}`);
      else toast.error(data.result?.reason || "Send failed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };
  if (!cfg) return <div className="text-sm text-slate-500 italic">Loading…</div>;
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="platform-settings">
      <div className="rounded-2xl border border-white/5 p-6 space-y-4" data-testid="platform-email-settings">
        <div className="text-label flex items-center gap-2"><Mail className="w-3 h-3" /> Platform email delivery (Resend)</div>
        <div className="grid grid-cols-2 gap-3">
          <KV label="Status" value={cfg.email_configured
            ? <span data-testid="platform-email-status" className="pill pill-success">Connected</span>
            : <span data-testid="platform-email-status" className="pill pill-danger">Not configured</span>} />
          <KV label="From address" value={<span data-testid="platform-sender-email">{cfg.sender_email || "—"}</span>} />
          <KV label="Sender name" value={cfg.sender_name || "—"} />
          <KV label="Stripe mode" value={<span className="capitalize">{cfg.stripe_mode || "—"}</span>} />
        </div>
        <p className="text-xs text-slate-500">Shops without their own Resend key send quotes, invoices and confirmations through this default.</p>
        <div className="flex gap-2">
          <input
            type="email" value={to} onChange={(e) => setTo(e.target.value)} data-testid="platform-test-email-input"
            placeholder="recipient@example.com"
            className="flex-1 bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
          />
          <button onClick={sendTest} disabled={sending || !to} data-testid="platform-test-email-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-sky-600 hover:bg-sky-500 text-white text-xs font-semibold px-4 py-2 disabled:opacity-50">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Send test
          </button>
        </div>
      </div>
      <div className="rounded-2xl border border-white/5 p-6 space-y-4" data-testid="platform-security-settings">
        <div className="text-label flex items-center gap-2"><Shield className="w-3 h-3" /> Security</div>
        <KV label="Master account" value={cfg.account_email} />
        <ul className="text-xs text-slate-400 space-y-1.5 list-disc pl-4">
          <li>This account can only open the platform console — shop pages and shop APIs are blocked.</li>
          <li>Viewing a shop is read-only and expires after 1 hour.</li>
          <li>The vault re-locks on every new tab or browser session.</li>
        </ul>
        <div className="flex flex-wrap gap-2">
        <button onClick={toggle} data-testid="platform-theme-toggle"
          className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-4 py-2">
          {theme === "light" ? <Moon className="w-3.5 h-3.5" /> : <Sun className="w-3.5 h-3.5" />}
          {theme === "light" ? "Switch to dark" : "Switch to light"}
        </button>
        <button onClick={onLock} data-testid="platform-settings-lock-btn"
          className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-4 py-2">
          <Lock className="w-3.5 h-3.5" /> Lock vault now
        </button>
        </div>
      </div>
    </div>
  );
}




function HeroStat({ label, value, sub, icon: Icon, tone }) {
  // Theme-adaptive tone palette — light-mode uses saturated 700-tier text
  // on pale backgrounds; dark-mode uses the 300-tier washes on tinted
  // glass. Both pass WCAG AA contrast against their own surfaces.
  const toneMap = {
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-500/[0.05] dark:text-emerald-300",
    blue:    "border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-400/30 dark:bg-blue-500/[0.05] dark:text-blue-300",
    violet:  "border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-400/30 dark:bg-violet-500/[0.05] dark:text-violet-300",
    sky:   "border-sky-300 bg-sky-50 text-sky-700 dark:border-sky-400/30 dark:bg-sky-500/[0.05] dark:text-sky-300",
    rose:    "border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-400/30 dark:bg-rose-500/[0.05] dark:text-rose-300",
    slate:   "border-slate-200 bg-slate-50 text-slate-700 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-300",
  };
  return (
    <div className={`rounded-2xl border p-5 ${toneMap[tone] || toneMap.slate}`}>
      <div className="flex items-center justify-between">
        <div className="text-label">{label}</div>
        <Icon className="w-4 h-4 opacity-80" />
      </div>
      <div className="font-heading text-4xl text-slate-900 dark:text-white font-light tracking-tighter mt-2">{value}</div>
      <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{sub}</div>
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  const toneMap = {
    info:   "text-blue-700 dark:text-blue-300",
    warn:   "text-sky-700 dark:text-sky-300",
    danger: "text-rose-700 dark:text-rose-300",
    muted:  "text-slate-500 dark:text-slate-400",
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 dark:border-white/5 dark:bg-white/[0.02] p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-xl font-semibold mt-0.5 ${toneMap[tone] || "text-slate-900 dark:text-white"}`}>{value}</div>
    </div>
  );
}


function BreakdownStat({ label, value, tone, negative = false }) {
  // Small per-metric breakdown line used inside the Net cash hero card.
  // `negative` just adds a leading "-" so Fees/Refunds read as deductions.
  const toneMap = {
    slate: "text-white",
    muted: "text-slate-300",
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-lg font-semibold mt-0.5 ${toneMap[tone] || "text-white"}`}>
        {negative && Number(value) > 0 ? "−" : ""}${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
      </div>
    </div>
  );
}


function RevenueSparkline({ data }) {
  // Minimal inline SVG sparkline — 12 months of net cash. The max is
  // dynamic so a tenant with low volume still reads nicely. No library
  // dependency; this is the only chart on the Vault today.
  const points = (data || []).map((d) => Number(d.net_aud) || 0);
  if (points.length === 0) return null;
  const max = Math.max(1, ...points);
  const min = Math.min(0, ...points);
  const range = Math.max(1, max - min);
  const w = 220, h = 60, pad = 4;
  const step = (w - pad * 2) / Math.max(1, points.length - 1);
  const scaleY = (v) => h - pad - ((v - min) / range) * (h - pad * 2);
  const path = points.map((v, i) => `${i === 0 ? "M" : "L"}${(pad + i * step).toFixed(1)},${scaleY(v).toFixed(1)}`).join(" ");
  // Area fill underneath the line — subtle emerald wash so the sparkline
  // reads as "positive cash" not just a random stat.
  const area = `${path} L${(pad + (points.length - 1) * step).toFixed(1)},${h - pad} L${pad},${h - pad} Z`;
  const latest = points[points.length - 1];
  return (
    <div className="shrink-0" title={`Last 12 months of net cash · latest $${latest.toLocaleString()}`}>
      <div className="text-[10px] uppercase tracking-widest text-emerald-300/70 text-right mb-1">12-mo trend</div>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="block">
        <defs>
          <linearGradient id="rev-grad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%"   stopColor="rgba(16,185,129,0.35)" />
            <stop offset="100%" stopColor="rgba(16,185,129,0)" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#rev-grad)" />
        <path d={path} fill="none" stroke="#34d399" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
        {/* Latest-month dot */}
        <circle
          cx={(pad + (points.length - 1) * step).toFixed(1)}
          cy={scaleY(latest).toFixed(1)}
          r="3"
          fill="#34d399"
        />
      </svg>
    </div>
  );
}


function StatCard({ icon: Icon, label, value, tone }) {
  const palette = {
    muted:   "border-white/10 bg-white/[0.03] text-slate-200",
    trial:   "border-sky-500/30 bg-sky-500/10 text-sky-100",
    success: "border-emerald-500/30 bg-emerald-500/10 text-emerald-100",
    warn:    "border-orange-500/30 bg-orange-500/10 text-orange-100",
    danger:  "border-red-500/30 bg-red-500/10 text-red-100",
  }[tone] || "border-white/10 bg-white/[0.03] text-slate-200";
  return (
    <div className={`rounded-2xl border p-4 ${palette}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-80 mb-2">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="font-heading text-2xl">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    trialing:  { label: "Trialing",  cls: "pill-warning" },
    active:    { label: "Active",    cls: "pill-success" },
    past_due:  { label: "Past due",  cls: "pill-danger"  },
    canceled:  { label: "Cancelled", cls: "pill-muted"   },
    suspended: { label: "Suspended", cls: "pill-danger"  },
  };
  const meta = map[status] || { label: status || "—", cls: "pill-muted" };
  return <span className={`pill ${meta.cls}`}>{meta.label}</span>;
}

function TenantDetailModal({ tenant, onClose }) {
  const [detail, setDetail] = useState(null);
  const [audit, setAudit] = useState([]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  useEffect(() => {
    (async () => {
      try {
        const [d, a] = await Promise.all([
          api.get(`/platform/tenants/${tenant.id}`),
          api.get("/platform/audit").catch(() => ({ data: [] })),
        ]);
        setDetail(d.data);
        setAudit((a.data || []).filter((r) => r.tenant_id === tenant.id));
      } catch (err) { toast.error("Failed to load details"); }
    })();
  }, [tenant.id]);

  return (
    <div
      data-testid="platform-detail-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="glass-strong rounded-3xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <div className="text-label">Shop detail</div>
            <h2 className="font-heading text-2xl text-white mt-1">{tenant.name}</h2>
            <div className="text-xs text-slate-500">{tenant.slug}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!detail ? (
          <div className="text-sm text-slate-500 italic">Loading…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 mb-5">
              <KV label="Plan" value={<span className="capitalize">{detail.plan}</span>} />
              <KV label="Status" value={<StatusPill status={detail.status} />} />
              <KV label="Owner" value={detail.owner_email || "—"} />
              <KV label="Created" value={detail.created_at ? new Date(detail.created_at).toLocaleDateString() : "—"} />
              <KV label="Stripe customer" value={detail.stripe_customer_id ? "✓ linked" : <span className="text-slate-500">—</span>} />
              <KV label="Stripe subscription" value={detail.stripe_subscription_id ? "✓ active" : <span className="text-slate-500">—</span>} />
              {detail.trial_ends_at && (
                <KV label="Trial ends" value={new Date(detail.trial_ends_at).toLocaleDateString()} />
              )}
              {detail.current_period_ends_at && (
                <KV label="Next renewal" value={new Date(detail.current_period_ends_at).toLocaleDateString()} />
              )}
            </div>

            <div className="text-label mb-2 flex items-center gap-2"><Users className="w-3 h-3" /> Members ({detail.members_count})</div>
            <div className="rounded-xl border border-white/5 overflow-hidden mb-4">
              {(detail.members || []).map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-white/5 last:border-0 text-xs">
                  <div className="min-w-0 flex-1">
                    <div className="text-white truncate">{m.name || m.email}</div>
                    <div className="text-slate-500 text-[11px] flex items-center gap-1.5"><Mail className="w-2.5 h-2.5" />{m.email}</div>
                  </div>
                  <span className={`pill ${m.role === "admin" ? "pill-info" : "pill-muted"} capitalize`}>{m.role}</span>
                  {m.is_owner && <span className="pill pill-info text-[9px]">OWNER</span>}
                </div>
              ))}
            </div>

            {audit.length > 0 && (
              <>
                <div className="text-label mb-2">Recent platform actions on this shop</div>
                <div className="rounded-xl border border-white/5 overflow-hidden">
                  {audit.slice(0, 5).map((a) => (
                    <div key={a.id} className="px-4 py-2 border-b border-white/5 last:border-0 text-[11px] text-slate-400">
                      <span className="text-white capitalize">{a.action.replace(/_/g, " ")}</span> by {a.actor_email}
                      {a.reason && <span className="text-slate-500"> — "{a.reason}"</span>}
                      <span className="text-slate-600 float-right">{a.created_at ? new Date(a.created_at).toLocaleString() : ""}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="rounded-xl bg-black/30 border border-white/5 p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className="text-sm text-white">{value}</div>
    </div>
  );
}
